import { ShieldX, ShieldAlert, Info, CheckCircle } from 'lucide-react';
import { FindingCard, type Finding } from './FindingCard';
import type { AnnotationAction } from '@/types';
import type { AnnotationMap } from './AnnotationForm';
import { cn } from '@/lib/utils';

interface FindingsPanelProps {
  findings: Finding[];
  annotations: AnnotationMap;
  onAnnotate: (findingId: string, action: AnnotationAction, comment: string) => void;
  className?: string;
}

interface SeverityGroup {
  severity: Finding['severity'];
  label: string;
  headerClass: string;
  Icon: React.FC<{ className?: string }>;
  emptyText: string;
}

const SEVERITY_GROUPS: SeverityGroup[] = [
  {
    severity: 'BLOCKING',
    label: 'Blocking Issues',
    headerClass: 'text-red-700 dark:text-red-400',
    Icon: ShieldX,
    emptyText: 'No blocking issues found.',
  },
  {
    severity: 'ALERT',
    label: 'Alerts',
    headerClass: 'text-amber-700 dark:text-amber-400',
    Icon: ShieldAlert,
    emptyText: 'No alerts.',
  },
  {
    severity: 'INFO',
    label: 'Informational',
    headerClass: 'text-blue-700 dark:text-blue-400',
    Icon: Info,
    emptyText: 'No informational findings.',
  },
];

function SeveritySection({
  group,
  items,
  annotations,
  onAnnotate,
}: {
  group: SeverityGroup;
  items: Finding[];
  annotations: AnnotationMap;
  onAnnotate: (findingId: string, action: AnnotationAction, comment: string) => void;
}) {
  const { Icon } = group;
  return (
    <section aria-labelledby={`findings-section-${group.severity.toLowerCase()}`}>
      <h3
        id={`findings-section-${group.severity.toLowerCase()}`}
        className={cn('flex items-center gap-2 text-sm font-semibold mb-2', group.headerClass)}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
        {group.label}
        <span className="text-xs font-normal text-muted-foreground">({items.length})</span>
      </h3>

      {items.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-3 px-4 rounded-md border border-dashed">
          <CheckCircle className="h-4 w-4 text-green-500" aria-hidden="true" />
          {group.emptyText}
        </div>
      ) : (
        <ul className="space-y-3" aria-label={`${group.label} list`}>
          {items.map((finding) => (
            <li key={finding.id}>
              <FindingCard
                finding={finding}
                annotations={annotations}
                onAnnotate={onAnnotate}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Groups findings by severity (BLOCKING → ALERT → INFO) and renders each
 * section with its header and per-finding annotation controls.
 *
 * Annotation completeness (blocking issues reviewed count) is derived by the
 * parent HumanReviewTab which passes it down; this panel is purely presentational.
 */
export function FindingsPanel({ findings, annotations, onAnnotate, className }: FindingsPanelProps) {
  const byGroup = (severity: Finding['severity']) =>
    findings.filter((f) => f.severity === severity);

  return (
    <div className={cn('space-y-6', className)} aria-label="Contract findings">
      {SEVERITY_GROUPS.map((group) => (
        <SeveritySection
          key={group.severity}
          group={group}
          items={byGroup(group.severity)}
          annotations={annotations}
          onAnnotate={onAnnotate}
        />
      ))}
    </div>
  );
}
